import { isPlatformServer } from '@angular/common';
import { Component, effect, inject, PLATFORM_ID } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { Meta, Title } from '@angular/platform-browser';
import { catchError, map, of, switchMap } from 'rxjs';
import { buildPageTitle } from '../../../shared/constants/site-meta';
import { BlogsService } from '../../../shared/services/blogs.service';
import { BlogRendererComponent } from '../../components/blog-renderer/blog-renderer.component';
import { MarketingBlogBreadcrumbComponent } from '../../components/marketing-blog-breadcrumb.component';
import { MarketingBlogGuideDownloadComponent } from '../../components/marketing-blog-guide-download.component';
import { MarketingConsultationCtaComponent } from '../../components/marketing-consultation-cta.component';
import { MarketingFooterComponent } from '../../components/marketing-footer.component';
import { MarketingHeaderComponent } from '../../components/marketing-header.component';

@Component({
  selector: 'app-marketing-blog-preview-page',
  standalone: true,
  imports: [
    RouterLink,
    MarketingHeaderComponent,
    MarketingBlogBreadcrumbComponent,
    BlogRendererComponent,
    MarketingBlogGuideDownloadComponent,
    MarketingConsultationCtaComponent,
    MarketingFooterComponent,
  ],
  templateUrl: './blog-preview-page.component.html',
})
export class MarketingBlogPreviewPageComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly blogs = inject(BlogsService);
  private readonly title = inject(Title);
  private readonly meta = inject(Meta);
  private readonly platformId = inject(PLATFORM_ID);

  readonly pageState = toSignal(
    this.route.paramMap.pipe(
      switchMap((params) => {
        const token = params.get('token');
        if (!token) return of({ kind: 'error' as const });
        return this.blogs.getPublicPreview(token).pipe(
          map((page) => ({ kind: 'ok' as const, page })),
          catchError(() =>
            isPlatformServer(this.platformId)
              ? of({ kind: 'pending' as const })
              : of({ kind: 'error' as const }),
          ),
        );
      }),
    ),
    { initialValue: { kind: 'pending' as const } },
  );

  constructor() {
    effect(() => {
      const s = this.pageState();
      if (s.kind === 'ok') {
        this.title.setTitle(buildPageTitle(`Preview: ${s.page.post.title}`));
        this.meta.updateTag({ name: 'robots', content: 'noindex,nofollow' });
      }
    });
  }

  sourcePage(slug: string): string {
    return `/blog/${slug}`;
  }
}
